// React
import { useEffect, useState } from 'react';

// Packages

// Utilities
import {
    useElseware,
    useLabels,
    useActivityTagging,
    useContext,
    useConfig,
    useModalState,
} from 'utilities/hooks';
import { useInitiativeDataStore } from 'utilities/store';
import { CONSTANTS } from 'utilities/configuration';

// Components
import { InputWrapper, FormFields } from 'components/_inputs';
import EmptyState from 'components/_wizard/emptyState';
import Button from 'components/button';
import TutorialBar from 'components/tutorialBar';
import DeleteModal from 'components/_modals/deleteModal';

const fieldSets = {
    _name({ object, prefix, required }) {
        return [
            {
                type: 'Text',
                name: `${prefix}___Name`,
                object: {
                    ...object.config(
                        'Initiative_Activity_Success_Metric__c.Name'
                    ),
                    required,
                },
                maxLength: 80,
            },
        ];
    },
    _target({ object, prefix, defaultValue = false }) {
        return [
            {
                type: 'Toggle',
                name: `${prefix}___Toggle___Target__c`,
                object: object.config(
                    'Initiative_Activity_Success_Metric__c.Target__c__Set'
                ),
                defaultValue,
                nested: {
                    fields() {
                        return [
                            {
                                type: 'Number',
                                name: `${prefix}___Target__c`,
                                object: object.config(
                                    'Initiative_Activity_Success_Metric__c.Target__c'
                                ),
                            },
                        ];
                    },
                    isActive() {
                        return false;
                    },
                },
            },
        ];
    },
    _currentStatus({ object, prefix, required, showCurrentStatus }) {
        return showCurrentStatus
            ? [
                  {
                      type: 'NumberFocused',
                      name: `${prefix}___Current_Status__c`,
                      theme: 'blue',
                      object: {
                          ...object.config(
                              'Initiative_Activity_Success_Metric__c.Current_Status__c'
                          ),
                          required,
                      },
                  },
              ]
            : [];
    },
    [CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE]({
        object,
        pickList,
        prefix,
        required,
        taggingFields,
        showCurrentStatus,
        indicatorMetricTargetOn,
    }) {
        return [
            ...taggingFields,
            {
                type: 'SelectList',
                name: `${prefix}___Gender__c`,
                object: object.config(
                    'Initiative_Activity_Success_Metric__c.Gender__c'
                ),
                textPlaceholder: object.label(
                    'Initiative_Activity_Success_Metric__c.Gender_Other__c'
                ),
                disableText(value) {
                    return value !== 'Other';
                },
                options: pickList(
                    'Initiative_Activity_Success_Metric__c.Gender__c'
                ),
                showText: true,
                listMaxLength: 1,
            },
            {
                type: 'Select',
                name: `${prefix}___Geographical_Reach__c`,
                object: object.config(
                    'Initiative_Activity_Success_Metric__c.Geographical_Reach__c'
                ),
                options: pickList(
                    'Initiative_Activity_Success_Metric__c.Geographical_Reach__c'
                ),
                sort: false,
            },
            ...this._target({
                object,
                prefix,
                defaultValue: indicatorMetricTargetOn,
            }),
            ...this._currentStatus({
                object,
                prefix,
                required,
                showCurrentStatus,
            }),
        ];
    },
    [`${CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE}_DISPLAY`]({
        object,
        prefix,
        required,
        item,
        showCurrentStatus,
        editButton,
        deleteButton,
    }) {
        return [
            {
                type: 'DisplayCard',
                name: `${prefix}___displayCard`,
                item,
                deleteButton,
                editButton,
                items: [
                    {
                        type: 'audienceTags',
                        label(item) {
                            return item.metricTitle;
                        },
                        tags(item) {
                            return item.audienceTags;
                        },
                    },
                    {
                        label(item) {
                            return object.label(
                                'Initiative_Activity_Success_Metric__c.Gender__c'
                            );
                        },
                        value(item) {
                            return item.gender;
                        },
                    },
                    {
                        label(item) {
                            return object.label(
                                'Initiative_Activity_Success_Metric__c.Geographical_Reach__c'
                            );
                        },
                        value(item) {
                            return item.reach;
                        },
                    },
                ],
            },
            ...[
                item.targetToggled
                    ? {
                          type: 'DisplayFocusedValue',
                          item,
                          label(item) {
                              return object.label(
                                  'Initiative_Activity_Success_Metric__c.Target__c'
                              );
                          },
                          value(item) {
                              return item.target;
                          },
                      }
                    : {},
            ],
            ...this._currentStatus({
                object,
                prefix,
                required,
                showCurrentStatus,
            }),
        ];
    },
    [CONSTANTS.ACTIVITY_SUCCESS_METRICS.FINANCIAL]({
        object,
        dataSet,
        prefix,
        required,
        taggingFields,
        showCurrentStatus,
        indicatorMetricTargetOn,
    }) {
        return [
            ...this._name({ object, prefix, required }),
            {
                type: 'Select',
                name: `${prefix}___CurrencyIsoCode`,
                object: object.config(
                    'Initiative_Activity_Success_Metric__c.CurrencyIsoCode'
                ),
                // Type options
                options: dataSet('Currencies'),
            },
            ...taggingFields,
            ...this._target({
                object,
                prefix,
                defaultValue: indicatorMetricTargetOn,
            }),
            ...this._currentStatus({
                object,
                prefix,
                required,
                showCurrentStatus,
            }),
        ];
    },
    [`${CONSTANTS.ACTIVITY_SUCCESS_METRICS.FINANCIAL}_DISPLAY`]({
        object,
        prefix,
        required,
        item,
        showCurrentStatus,
        editButton,
        deleteButton,
    }) {
        return [
            {
                type: 'DisplayCard',
                name: `${prefix}___displayCard`,
                item,
                editButton,
                deleteButton,
                items: [
                    {
                        label(item) {
                            return object.label(
                                'Initiative_Activity_Success_Metric__c.Name'
                            );
                        },
                        value(item) {
                            return item.name;
                        },
                    },
                    {
                        label(item) {
                            return object.label(
                                'Initiative_Activity_Success_Metric__c.CurrencyIsoCode'
                            );
                        },
                        value(item) {
                            return item.currency;
                        },
                    },
                    {
                        type: 'tags',
                        label(item) {
                            return item.tagsLabel;
                        },
                        tags(item) {
                            return item.tags;
                        },
                    },
                ],
            },
            ...[
                item.targetToggled
                    ? {
                          type: 'DisplayFocusedValue',
                          item,
                          label(item) {
                              return object.label(
                                  'Initiative_Activity_Success_Metric__c.Target__c'
                              );
                          },
                          value(item) {
                              return item.target;
                          },
                      }
                    : {},
            ],
            ...this._currentStatus({
                object,
                prefix,
                required,
                showCurrentStatus,
            }),
        ];
    },
    [CONSTANTS.ACTIVITY_SUCCESS_METRICS.CUSTOM]({
        object,
        prefix,
        required,
        taggingFields,
        showCurrentStatus,
        indicatorMetricTargetOn,
    }) {
        return [
            ...this._name({ object, prefix, required }),
            ...taggingFields,
            ...this._target({
                object,
                prefix,
                defaultValue: indicatorMetricTargetOn,
            }),
            ...this._currentStatus({
                object,
                prefix,
                required,
                showCurrentStatus,
            }),
        ];
    },
    [`${CONSTANTS.ACTIVITY_SUCCESS_METRICS.CUSTOM}_DISPLAY`]({
        object,
        prefix,
        required,
        item,
        showCurrentStatus,
        editButton,
        deleteButton,
    }) {
        return [
            {
                type: 'DisplayCard',
                name: `${prefix}___displayCard`,
                item,
                editButton,
                deleteButton,
                items: [
                    {
                        label(item) {
                            return object.label(
                                'Initiative_Activity_Success_Metric__c.Name'
                            );
                        },
                        value(item) {
                            return item.name;
                        },
                    },
                    {
                        type: 'tags',
                        label(item) {
                            return item.tagsLabel;
                        },
                        tags(item) {
                            return item.tags;
                        },
                    },
                ],
            },
            ...(item.targetToggled
                ? [
                      {
                          type: 'DisplayFocusedValue',
                          item,
                          label(item) {
                              return object.label(
                                  'Initiative_Activity_Success_Metric__c.Target__c'
                              );
                          },
                          value(item) {
                              return item.target;
                          },
                      },
                  ]
                : []),
            ...this._currentStatus({
                object,
                prefix,
                required,
                showCurrentStatus,
            }),
        ];
    },
    ERROR_DISPLAY({ prefix, error }) {
        return [
            {
                type: 'ErrorCard',
                name: `${prefix}___errorCard`,
                error,
            },
        ];
    },
};

const ActivityMetrics = ({
    Name,
    primary = false,
    secondary = false,
    additional = false,
    allowSkipMetrics = false,
    initComponent,
    form,
    inputState,
}) => {
    // ///////////////////
    // STORES
    // ///////////////////

    const { utilities } = useInitiativeDataStore();
    const { CONTEXTS, CONTEXT } = useContext();

    // ///////////////////
    // STATE
    // ///////////////////

    const [init, setInit] = useState(0);
    const [primaryMetricToggle, setPrimaryMetricToggle] = useState(false);
    const [secondaryMetricToggle, setSecondaryMetricToggle] = useState(false);
    const [additionalMetricToggle, setAdditionalMetricToggle] = useState(false);
    const [additionalMetricType, setAdditionalMetricType] = useState(
        CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE
    );
    const [deleteId, setDeleteId] = useState(null);
    const [deletePrefix, setDeletePrefix] = useState(null);
    const [metricsView, setMetricsView] = useState([0, 0]); // Not applicable [0,0], None [1,0], Yes [1,1]
    const {
        modalState: deleteModalState,
        modalOpen: deleteModalOpen,
        modalClose: deleteModalClose,
        modalSaving: deleteModalSaving,
        modalNotSaving: deleteModalNotSaving,
    } = useModalState();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { indicatorMetricTargetOn } = useConfig({
        initiativeType: utilities.initiative.get().Configuration_Type__c,
        activityType: Name,
    });
    const { ewUpdate, ewCreate, ewDelete } = useElseware();
    const { object, pickList, dataSet, label, getValueLabel } = useLabels();

    const tagging = useActivityTagging({
        types: [CONSTANTS.TAGS.INTERVENTION_ACTIVITY],
    });

    // ///////////////////
    // METHODS
    // ///////////////////

    function getItemData(formData) {
        const {
            Primary___Gender__c,
            Primary___Geographical_Reach__c,
            Primary___Toggle___Target__c,
            Primary___Target__c,
            Primary___Current_Status__c,

            Secondary___Gender__c,
            Secondary___Geographical_Reach__c,
            Secondary___Toggle___Target__c,
            Secondary___Target__c,
            Secondary___Current_Status__c,

            Additional___Type__c,
            Additional___Name,
            Additional___Gender__c,
            Additional___Geographical_Reach__c,
            Additional___Toggle___Target__c,
            Additional___Target__c,
            Additional___Current_Status__c,
            Additional___CurrencyIsoCode,
        } = formData;

        return {
            primary: {
                Name: CONSTANTS.ACTIVITY_SUCCESS_METRICS.NAME_PRIMARY,
                Type__c: CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE,
                Priority__c:
                    CONSTANTS.ACTIVITY_SUCCESS_METRICS.PRIORITY_PRIMARY,
                Gender__c: Primary___Gender__c
                    ? Primary___Gender__c[0]?.selectValue
                    : null,
                Gender_Other__c: Primary___Gender__c
                    ? Primary___Gender__c[0]?.textValue
                    : null,
                Geographical_Reach__c: Primary___Geographical_Reach__c,
                Target__c: Primary___Toggle___Target__c
                    ? Primary___Target__c
                    : null,
                Current_Status__c: Primary___Current_Status__c,
            },
            secondary: {
                Name: CONSTANTS.ACTIVITY_SUCCESS_METRICS.NAME_SECONDARY,
                Type__c: CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE,
                Priority__c:
                    CONSTANTS.ACTIVITY_SUCCESS_METRICS.PRIORITY_SECONDARY,
                Gender__c: Secondary___Gender__c
                    ? Secondary___Gender__c[0]?.selectValue
                    : null,
                Gender_Other__c: Secondary___Gender__c
                    ? Secondary___Gender__c[0]?.textValue
                    : null,
                Geographical_Reach__c: Secondary___Geographical_Reach__c,
                Target__c: Secondary___Toggle___Target__c
                    ? Secondary___Target__c
                    : null,
                Current_Status__c: Secondary___Current_Status__c,
            },
            additional: {
                Name:
                    Additional___Type__c ===
                    CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE
                        ? CONSTANTS.ACTIVITY_SUCCESS_METRICS.NAME_ADDITIONAL
                        : Additional___Name ??
                          CONSTANTS.ACTIVITY_SUCCESS_METRICS.NAME_ADDITIONAL,
                Type__c: Additional___Type__c,
                Priority__c:
                    CONSTANTS.ACTIVITY_SUCCESS_METRICS.PRIORITY_ADDITIONAL,
                Gender__c: Additional___Gender__c
                    ? Additional___Gender__c[0]?.selectValue
                    : null,
                Gender_Other__c: Additional___Gender__c
                    ? Additional___Gender__c[0]?.textValue
                    : null,
                Geographical_Reach__c: Additional___Geographical_Reach__c,
                Target__c: Additional___Toggle___Target__c
                    ? Additional___Target__c
                    : null,
                Current_Status__c: Additional___Current_Status__c,
                CurrencyIsoCode: Additional___CurrencyIsoCode,
            },
        };
    }

    async function _addPrimaryAudience(formData) {
        // Get saved data
        const { Activity___Id } = formData;

        // Primary audience
        const { data: primaryItemData } = await ewCreate(
            'initiative-activity-success-metric/initiative-activity-success-metric',
            {
                ...getItemData(formData)?.primary,
                Initiative_Activity__c: Activity___Id,
            }
        );

        utilities.updateInitiativeData(
            '_activitySuccessMetrics',
            primaryItemData
        );

        await tagging.primaryMetric.submit(formData, primaryItemData);
    }

    async function _addSecondaryAudience(formData) {
        // Get saved data
        const { Activity___Id } = formData;

        const { data: secondaryItemData } = await ewCreate(
            'initiative-activity-success-metric/initiative-activity-success-metric',
            {
                ...getItemData(formData)?.secondary,
                Initiative_Activity__c: Activity___Id,
            }
        );

        utilities.updateInitiativeData(
            '_activitySuccessMetrics',
            secondaryItemData
        );

        await tagging.secondaryMetric.submit(formData, secondaryItemData);
    }

    async function _addAdditionalMetric(formData) {
        // Get saved data
        const { Activity___Id } = formData;

        const { data: additionalItemData } = await ewCreate(
            'initiative-activity-success-metric/initiative-activity-success-metric',
            {
                ...getItemData(formData)?.additional,
                Initiative_Activity__c: Activity___Id,
            }
        );

        utilities.updateInitiativeData(
            '_activitySuccessMetrics',
            additionalItemData
        );

        switch (additionalMetricType) {
            case CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE:
                await tagging.additionalMetricPeople.submit(
                    formData,
                    additionalItemData
                );
                break;
            case CONSTANTS.ACTIVITY_SUCCESS_METRICS.FINANCIAL:
                await tagging.additionalMetricFinancial.submit(
                    formData,
                    additionalItemData
                );
                break;
            case CONSTANTS.ACTIVITY_SUCCESS_METRICS.CUSTOM:
                await tagging.additionalMetricCustom.submit(
                    formData,
                    additionalItemData
                );
                break;
        }
    }

    async function addItem(formData) {
        const {
            Primary__Toggle,
            Secondary__Toggle,
            Additional__Toggle,
        } = formData;

        try {
            // Primary audience
            if (primary && Primary__Toggle) {
                await _addPrimaryAudience(formData);
            }
            // Secondary audience
            if (secondary && Secondary__Toggle) {
                await _addSecondaryAudience(formData);
            }

            // Additional metric
            if (additional && Additional__Toggle) {
                await _addAdditionalMetric(formData);
            }
        } catch (error) {
            console.log(error);
            return error;
        }
    }

    async function editItem(formData) {
        const {
            Primary__Toggle,
            Primary___Metric_Id,
            Secondary___Metric_Id,
            Additional___Metric_Id,
            Secondary__Toggle,
            Additional__Toggle,
            Additional___Type__c,
        } = formData;

        try {
            // Primary audience
            if (primary && Primary__Toggle) {
                if (Primary___Metric_Id) {
                    // Reset tags
                    // await tagging.primaryMetric.remove(
                    //     utilities.activitySuccessMetrics.get(
                    //         Primary___Metric_Id
                    //     )
                    // );

                    const { data: primaryItemData } = await ewUpdate(
                        'initiative-activity-success-metric/initiative-activity-success-metric',
                        Primary___Metric_Id,
                        getItemData(formData)?.primary
                    );

                    utilities.updateInitiativeData(
                        '_activitySuccessMetrics',
                        primaryItemData
                    );

                    await tagging.primaryMetric.submit(
                        formData,
                        primaryItemData
                    );
                } else {
                    await _addPrimaryAudience(formData);
                }
            }

            // Secondary audience
            if (secondary && Secondary__Toggle) {
                if (Secondary___Metric_Id) {
                    // Reset tags
                    // await tagging.primaryMetric.remove(
                    //     utilities.activitySuccessMetrics.get(
                    //         Secondary___Metric_Id
                    //     )
                    // );

                    const { data: secondaryItemData } = await ewUpdate(
                        'initiative-activity-success-metric/initiative-activity-success-metric',
                        Secondary___Metric_Id,
                        getItemData(formData)?.secondary
                    );

                    utilities.updateInitiativeData(
                        '_activitySuccessMetrics',
                        secondaryItemData
                    );

                    await tagging.secondaryMetric.submit(
                        formData,
                        secondaryItemData
                    );
                } else {
                    await _addSecondaryAudience(formData);
                }
            }

            // Additional metric
            if (additional && Additional__Toggle) {
                if (Additional___Metric_Id) {
                    // Reset tags
                    // await tagging.primaryMetric.remove(
                    //     utilities.activitySuccessMetrics.get(
                    //         Additional___Metric_Id
                    //     )
                    // );

                    const { data: additionalItemData } = await ewUpdate(
                        'initiative-activity-success-metric/initiative-activity-success-metric',
                        Additional___Metric_Id,
                        getItemData(formData)?.additional
                    );

                    utilities.updateInitiativeData(
                        '_activitySuccessMetrics',
                        additionalItemData
                    );

                    switch (Additional___Type__c) {
                        case CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE:
                            await tagging.additionalMetricPeople.submit(
                                formData,
                                additionalItemData
                            );
                            break;
                        case CONSTANTS.ACTIVITY_SUCCESS_METRICS.FINANCIAL:
                            await tagging.additionalMetricFinancial.submit(
                                formData,
                                additionalItemData
                            );
                            break;
                        case CONSTANTS.ACTIVITY_SUCCESS_METRICS.CUSTOM:
                            await tagging.additionalMetricCustom.submit(
                                formData,
                                additionalItemData
                            );
                            break;
                    }
                } else {
                    await _addAdditionalMetric(formData);
                }
            }
        } catch (error) {
            console.log(error);
            return error;
        }
    }

    async function deleteItem() {
        // Modal save button state
        deleteModalSaving();
        try {
            // Do the delete
            await ewDelete(
                'initiative-activity-success-metric/initiative-activity-success-metric',
                deleteId
            );

            // Clean out item
            utilities.removeInitiativeData('_activitySuccessMetrics', deleteId);

            // Remove from form
            switch (deletePrefix) {
                case 'Primary':
                    form.setValue('Primary__Toggle', false);
                    form.setValue('Primary___Current_Status__c', null);
                    form.setValue('Primary___Gender__c', null);
                    form.setValue('Primary___Geographical_Reach__c', null);
                    form.setValue('Primary___Metric_Id', null);
                    form.setValue('Primary___Target__c', null);
                    form.setValue('Primary___Toggle___Target__c', null);
                case 'Secondary':
                    form.setValue('Secondary__Toggle', false);
                    form.setValue('Secondary___Current_Status__c', null);
                    form.setValue('Secondary___Gender__c', null);
                    form.setValue('Secondary___Geographical_Reach__c', null);
                    form.setValue('Secondary___Metric_Id', null);
                    form.setValue('Secondary___Target__c', null);
                    form.setValue('Secondary___Toggle___Target__c', null);
                    break;
                case 'Additional':
                    form.setValue('Additional__Toggle', false);
                    form.setValue('Additional___Type__c', null);
                    form.setValue('Additional___Name', null);
                    form.setValue('Additional___Gender__c', null);
                    form.setValue('Additional___Geographical_Reach__c', null);
                    form.setValue('Additional___Toggle___Target__c', null);
                    form.setValue('Additional___Target__c', null);
                    form.setValue('Additional___Current_Status__c', null);
                    form.setValue('Additional___CurrencyIsoCode', null);
                    break;
                default:
                    break;
            }

            // Close modal
            deleteModalClose();

            // Modal save button state
            deleteModalNotSaving();

            // Reset delete id + prefix
            setDeleteId(null);
            setDeletePrefix(null);
        } catch (error) {
            // Modal save button state
            deleteModalNotSaving();
            console.warn(error);
        }
    }

    function getGenderFromItem(item, prefix) {
        if (item[`${prefix}___Gender__c`]) {
            const g = item[`${prefix}___Gender__c`][0] ?? null;

            if (g?.selectValue) {
                return `${getValueLabel(
                    'Initiative_Activity_Success_Metric__c.Gender__c',
                    g?.selectValue
                )}${g?.textValue ? ` (${g?.textValue})` : ''}`;
            }
        }

        return null;
    }

    // ///////////////////
    // DATA
    // ///////////////////

    const {
        Primary___Metric_Id,
        Secondary___Metric_Id,
        Additional___Metric_Id,
    } = form.getValues();

    const { isAddMode, isEditMode, isEditing, setIsEditing } = inputState;
    const isDisplayMode = isEditMode && !isEditing;
    const showCurrentStatus = CONTEXT === CONTEXTS.REPORT;

    // Has metrics is based on editMode

    // ///////////////////
    // COMPONENTS
    // ///////////////////

    const EditButton = () => (
        <Button
            variant="secondary"
            theme="teal"
            action={() => setIsEditing(true)}
            disabled={isEditing}>
            {label('ButtonEdit')}
        </Button>
    );

    const DeleteButton = ({ id, prefix }) => (
        <Button
            variant="tertiary"
            theme="teal"
            action={() => {
                setDeleteId(id);
                setDeletePrefix(prefix);
                deleteModalOpen();
            }}
            disabled={isEditing}>
            {label('ButtonDelete')}
        </Button>
    );

    // ///////////////////
    // EFFECTS
    // ///////////////////

    // Metrics: Not applicable [0,0], None [1,0], Yes [1,1]
    useEffect(() => {
        const canMetrics = primary || secondary || additional ? 1 : 0;

        // Check for db values and soft values from modal interaction before save
        const hasMetrics = [
            Primary___Metric_Id,
            Secondary___Metric_Id,
            Additional___Metric_Id,
            tagging.primaryMetric.getTagsFromFieldValue(form.getValues())
                .length > 0,
            tagging.primaryMetric.getTagsFromFieldValue(form.getValues())
                .length > 0,
            form.getValues().Additional___Type__c,
        ].some(x => x)
            ? 1
            : 0;

        setMetricsView([canMetrics, hasMetrics]);
    }, [
        isEditing,
        Primary___Metric_Id,
        Secondary___Metric_Id,
        Additional___Metric_Id,
        primary,
        secondary,
        additional,
    ]);

    useEffect(() => {
        setInit(init + 1);
    }, [
        primaryMetricToggle,
        secondaryMetricToggle,
        additionalMetricToggle,
        additionalMetricType,
        isDisplayMode,
    ]);

    useEffect(() => {
        // Check if there are metrics
        const hasMetrics = primary || secondary || additional;

        // Check if there are tags
        const hasTagging = tagging.primaryMetric.metricFields.length > 0;

        // Check if there are toggles for the metrics (enabled metrics)
        // This goes for all conditions and allows it to move forward via the next button
        const hasMetricsToggle =
            primaryMetricToggle ||
            secondaryMetricToggle ||
            additionalMetricToggle;

        // allowTrigger default is opposite of hasMetrics, as the check only needs to run if there are metrics enabled
        // let allowTrigger = !hasMetrics;
        let allowTrigger = false;

        if (hasMetrics && (isAddMode || isEditMode)) {
            if (isAddMode) {
                allowTrigger = hasMetricsToggle;
            } else if (isEditMode) {
                allowTrigger = hasTagging;
            }
        }

        if (!hasMetrics || allowSkipMetrics) {
            allowTrigger = true;
        }

        initComponent({
            fields: fields(),
            methods: {
                add: { metrics: addItem },
                edit: { metrics: editItem },
            },
            allow: {
                name: 'metrics',
                trigger: allowTrigger,
            },
        });
    }, [
        init,
        tagging.primaryMetric.metricFields,
        primaryMetricToggle,
        secondaryMetricToggle,
        additionalMetricToggle,
        CONTEXT,
    ]);

    // ///////////////////
    // FIELDS
    // ///////////////////

    const fieldSetDefaultParams = { object, dataSet, pickList };

    function primaryFields() {
        const {
            Primary__Toggle,
            Primary___Geographical_Reach__c,
            Primary___Target__c,
            Primary___Toggle___Target__c,
        } = form.getValues();

        const item = {
            metricTitle: label('ActivityBuilderMetric_PrimaryAudience_Label'),
            audienceTags: tagging.primaryMetric.getTagsFromFieldValue(
                form.getValues()
            ),
            gender: getGenderFromItem(form.getValues(), 'Primary'),
            reach: Primary___Geographical_Reach__c,
            target: Primary___Target__c,
            targetToggled: Primary___Toggle___Target__c,
        };

        // Show displayMode if it is displayMode and primary audience is toggled
        if (Primary__Toggle && isDisplayMode && Primary___Metric_Id) {
            return [
                {
                    type: 'Nested',
                    name: 'Primary___Nested',
                    fields: [
                        ...fieldSets[
                            `${CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE}_DISPLAY`
                        ]({
                            ...fieldSetDefaultParams,
                            prefix: 'Primary',
                            required: Primary__Toggle,
                            item,
                            showCurrentStatus,
                            editButton: <EditButton />,
                            deleteButton: allowSkipMetrics ? (
                                <DeleteButton
                                    id={Primary___Metric_Id}
                                    prefix="Primary"
                                />
                            ) : null,
                        }),
                    ],
                },
            ];
        }

        return [
            {
                type: 'Toggle',
                name: 'Primary__Toggle',
                object: {
                    label: label(
                        'ActivityBuilderMetricToggle_PrimaryAudience_Label'
                    ),
                    subLabel: label(
                        'ActivityBuilderMetricToggle_PrimaryAudience_SubLabel'
                    ),
                },
                onWatch(event) {
                    setPrimaryMetricToggle(event);
                    // Close secondary if primary is toggled off
                    if (!event) {
                        form.setValue('Secondary__Toggle', false);
                    }
                },
                nested: {
                    fields() {
                        return [
                            ...fieldSets[
                                CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE
                            ]({
                                ...fieldSetDefaultParams,
                                prefix: 'Primary',
                                required: primaryMetricToggle,
                                taggingFields:
                                    tagging.primaryMetric.metricFields,
                                showCurrentStatus,
                                indicatorMetricTargetOn: indicatorMetricTargetOn(),
                            }),
                        ];
                    },
                    isActive() {
                        return primaryMetricToggle;
                    },
                },
            },
        ];
    }

    function secondaryFields() {
        const {
            Primary__Toggle,
            Secondary__Toggle,
            Secondary___Geographical_Reach__c,
            Secondary___Target__c,
            Secondary___Toggle___Target__c,
        } = form.getValues();

        const item = {
            metricTitle: label('ActivityBuilderMetric_SecondaryAudience_Label'),
            audienceTags: tagging.secondaryMetric.getTagsFromFieldValue(
                form.getValues()
            ),
            gender: getGenderFromItem(form.getValues(), 'Secondary'),
            reach: Secondary___Geographical_Reach__c,
            target: Secondary___Target__c,
            targetToggled: Secondary___Toggle___Target__c,
        };

        // Show displayMode if it is displayMode and secondary audience is toggled
        if (Secondary__Toggle && isDisplayMode && Secondary___Metric_Id) {
            return [
                {
                    type: 'Nested',
                    name: 'Secondary___Nested',
                    fields: [
                        ...fieldSets[
                            `${CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE}_DISPLAY`
                        ]({
                            ...fieldSetDefaultParams,
                            prefix: 'Secondary',
                            required: Secondary__Toggle,
                            item,
                            showCurrentStatus,
                            editButton: <EditButton />,
                            deleteButton: (
                                <DeleteButton
                                    id={Secondary___Metric_Id}
                                    prefix="Secondary"
                                />
                            ),
                        }),
                    ],
                },
            ];
        }

        return Primary__Toggle
            ? [
                  {
                      type: 'Toggle',
                      name: 'Secondary__Toggle',
                      object: {
                          label: label(
                              'ActivityBuilderMetricToggle_SecondaryAudience_Label'
                          ),
                          subLabel: label(
                              'ActivityBuilderMetricToggle_SecondaryAudience_SubLabel'
                          ),
                      },
                      onWatch(event) {
                          setSecondaryMetricToggle(event);
                      },
                      nested: {
                          fields() {
                              return [
                                  ...fieldSets[
                                      CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE
                                  ]({
                                      ...fieldSetDefaultParams,
                                      prefix: 'Secondary',
                                      required: secondaryMetricToggle,
                                      taggingFields:
                                          tagging.secondaryMetric.metricFields,
                                      showCurrentStatus,
                                      indicatorMetricTargetOn: indicatorMetricTargetOn(),
                                  }),
                              ];
                          },
                          isActive() {
                              return secondaryMetricToggle;
                          },
                      },
                  },
              ]
            : [];
    }

    function additionalFields() {
        const {
            Additional__Toggle,
            Additional___Name,
            Additional___Geographical_Reach__c,
            Additional___Target__c,
            Additional___Toggle___Target__c,
            Additional___Type__c,
            Additional___CurrencyIsoCode,
        } = form.getValues();

        const item = {
            metricTitle: label('ActivityBuilderMetric_AdditionalMetric_Label'),
            gender: getGenderFromItem(form.getValues(), 'Additional'),
            reach: Additional___Geographical_Reach__c,
            type: Additional___Type__c,
            name: Additional___Name,
            currency: Additional___CurrencyIsoCode,
            target: Additional___Target__c,
            targetToggled: Additional___Toggle___Target__c,
            tagsLabel: label('DetailsTagsMetrics'),
        };

        // Default tagging
        let additionalTaggingFields =
            tagging.additionalMetricPeople.metricFields;

        item.audienceTags = tagging.additionalMetricPeople.getTagsFromFieldValue(
            form.getValues()
        );

        switch (Additional___Type__c) {
            case CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE:
                additionalTaggingFields =
                    tagging.additionalMetricPeople.metricFields;
                item.audienceTags = tagging.additionalMetricPeople.getTagsFromFieldValue(
                    form.getValues()
                );
                break;
            case CONSTANTS.ACTIVITY_SUCCESS_METRICS.FINANCIAL:
                additionalTaggingFields =
                    tagging.additionalMetricFinancial.fields;
                item.tags = tagging.additionalMetricFinancial.getTagsFromFieldValue(
                    form.getValues()
                );
                break;
            case CONSTANTS.ACTIVITY_SUCCESS_METRICS.CUSTOM:
                additionalTaggingFields = tagging.additionalMetricCustom.fields;
                item.tags = tagging.additionalMetricCustom.getTagsFromFieldValue(
                    form.getValues()
                );
                break;
        }

        // Show displayMode if it is displayMode and additional metric is toggled
        if (Additional__Toggle && isDisplayMode && Additional___Metric_Id) {
            return [
                {
                    type: 'Nested',
                    name: 'Additional___Nested',
                    fields: [
                        ...fieldSets[`${Additional___Type__c}_DISPLAY`]({
                            ...fieldSetDefaultParams,
                            prefix: 'Additional',
                            required: Additional__Toggle,
                            taggingFields: additionalTaggingFields,
                            item,
                            showCurrentStatus,
                            editButton: <EditButton />,
                            deleteButton: (
                                <DeleteButton
                                    id={Additional___Metric_Id}
                                    prefix="Additional"
                                />
                            ),
                        }),
                    ],
                },
            ];
        }

        // Is this enabled or not? Always on if alone, but depending on primary and secondary to be toggled if they exist
        const peopleEnabled = [
            !primary && !secondary,
            primary && !secondary && primaryMetricToggle,
            !primary && secondary && secondaryMetricToggle,
            primary &&
                secondary &&
                primaryMetricToggle &&
                secondaryMetricToggle,
        ].some(x => x);

        // Only allow value = "People" if enabled above
        const options = pickList(
            'Initiative_Activity_Success_Metric__c.Type__c'
        ).map(x => {
            if (x.value === CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE) {
                return {
                    ...x,
                    optionDisabled: !peopleEnabled,
                };
            }
            return x;
        });

        return [
            {
                type: 'Toggle',
                name: 'Additional__Toggle',
                object: {
                    label: label(
                        'ActivityBuilderMetricToggle_AdditionalMetric_Label'
                    ),
                    subLabel: label(
                        'ActivityBuilderMetricToggle_AdditionalMetric_SubLabel'
                    ),
                },
                onWatch(event) {
                    setAdditionalMetricToggle(event);
                },
                nested: {
                    fields() {
                        return [
                            {
                                type: 'Select',
                                name: 'Additional___Type__c',
                                object: object.config(
                                    'Initiative_Activity_Success_Metric__c.Type__c'
                                ),
                                defaultValue: peopleEnabled
                                    ? CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE
                                    : CONSTANTS.ACTIVITY_SUCCESS_METRICS
                                          .FINANCIAL,
                                options,
                                onWatch(event, form) {
                                    // Update type
                                    setAdditionalMetricType(event);
                                },
                            },
                            ...fieldSets[additionalMetricType]({
                                ...fieldSetDefaultParams,
                                prefix: 'Additional',
                                required: additionalMetricToggle,
                                taggingFields: additionalTaggingFields,
                                showCurrentStatus,
                                indicatorMetricTargetOn: indicatorMetricTargetOn(),
                            }),
                        ];
                    },
                    isActive() {
                        return additionalMetricToggle;
                    },
                },
            },
        ];
    }

    function fields() {
        return tagging.primaryMetric.metricFields.length > 0
            ? [
                  ...(primary ? primaryFields() : []),
                  ...(secondary ? secondaryFields() : []),
                  ...(additional && additionalMetricType
                      ? additionalFields()
                      : []),
              ]
            : [
                  ...fieldSets['ERROR_DISPLAY']({
                      prefix: 'Primary',
                      error: label(
                          'MetricsNoPeopleMetricsSetupForThisInitiative'
                      ),
                  }),
              ];
    }

    // ///////////////////
    // RENDER
    // ///////////////////

    const [canMetrics, hasMetrics] = metricsView;

    return canMetrics ? (
        <>
            {(isEditMode && hasMetrics) ||
            (isEditMode && allowSkipMetrics) ||
            isEditing ||
            isAddMode ? (
                <>
                    <TutorialBar tutorialKey="ActivityMetrics" theme="teal" />
                    <InputWrapper>
                        <FormFields
                            {...{
                                fields: fields(),
                                form,
                            }}
                        />
                    </InputWrapper>
                    <DeleteModal
                        {...{
                            onCancel() {
                                deleteModalClose();
                            },
                            async onDelete() {
                                await deleteItem();
                            },
                            title: label(
                                'WizardModalHeadingActivityMetricsDelete'
                            ),
                            text: label('WizardModalTextActivityMetricsDelete'),
                            ...deleteModalState,
                        }}
                    />
                </>
            ) : (
                <EmptyState
                    {...{
                        text: label('MetricUpdatesNoMetrics'),
                    }}
                />
            )}
        </>
    ) : (
        <EmptyState
            {...{
                text: label('MetricUpdatesNotApplicable'),
            }}
        />
    );
};

export default ActivityMetrics;
